import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import WelcomeScreen from './components/WelcomeScreen';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import GameDashboard from './components/GameDashboard';
import QuestMap from './components/QuestMap';
import Profile from './components/Profile';
import ParentDashboard from './components/ParentDashboard';
import MathGame from './components/games/MathGame';
import NumberJumpsGame from './components/games/NumberJumpsGame';
import ScienceGame from './components/games/ScienceGame';
import ShapeGame from './components/games/ShapeGame';
import ColorGame from './components/games/ColorGame';
import CountingGame from './components/games/CountingGame';
import PatternGame from './components/games/PatternGame';
import MemoryGame from './components/games/MemoryGame';
import LogicGame from './components/games/LogicGame';
import MultiplicationIslandGame from './components/games/MultiplicationIslandGame';
import MultiplicationTreasureHuntGame from './components/games/MultiplicationTreasureHuntGame';
import MultiplicationJungleGame from './components/games/MultiplicationJungleGame';
import MultiplicationSudokuGame from './components/games/MultiplicationSudokuGame';
import ReadingChallengeGame from './components/games/ReadingChallengeGame';
import CleanupQuestGame from './components/games/CleanupQuestGame';
import AvatarCustomization from './components/AvatarCustomization';
import Settings from './components/Settings';
import Avatar from './components/Avatar';
import NavigationBar from './components/common/NavigationBar';
import { GameProvider, useGame } from './context/GameContext';
import './App.css';

// SEL Games
import SELTeddyPlayground from './components/games/sel/SELTeddyPlayground';
import SELFeelingsFaces from './components/games/sel/SELFeelingsFaces';
import SELBunnyChoices from './components/games/sel/SELBunnyChoices';
import SELCalmBubbles from './components/games/sel/SELCalmBubbles';

function AppRoutes() {
  const { state } = useGame();

  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jungle-50">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🦁</div>
          <p className="font-kid text-jungle-600">Loading your adventure...</p>
        </div>
      </div>
    );
  }

  const isAuthenticated = !!state.user;

  return (
    <AnimatePresence mode="wait">
      <Routes>
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <WelcomeScreen />} />
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" /> : <SignUp />} />
        <Route path="/dashboard" element={isAuthenticated ? <GameDashboard /> : <Navigate to="/login" />} />
        <Route path="/quest-map" element={isAuthenticated ? <QuestMap /> : <Navigate to="/login" />} />
        <Route path="/profile" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
        <Route path="/parent" element={isAuthenticated ? <ParentDashboard /> : <Navigate to="/login" />} />
        <Route path="/settings" element={isAuthenticated ? <Settings /> : <Navigate to="/login" />} />
        <Route path="/avatar" element={isAuthenticated ? <AvatarCustomization /> : <Navigate to="/login" />} />
        <Route path="/game/:gameId" element={isAuthenticated ? <GameResolver /> : <Navigate to="/login" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </AnimatePresence>
  );
}

function GameResolver() {
  const { gameId } = useParams();
  const games = {
    // Classic games
    'reading-challenge': <ReadingChallengeGame />,
    'math': <MathGame />,
    'number-jumps': <NumberJumpsGame />,
    'science': <ScienceGame />,
    'shapes': <ShapeGame />,
    'colors': <ColorGame />,
    'counting': <CountingGame />,
    'patterns': <PatternGame />,
    'memory': <MemoryGame />,
    'logic': <LogicGame />,
    'multiplication-island': <MultiplicationIslandGame />,
    'multiplication-treasure-hunt': <MultiplicationTreasureHuntGame />,
    'multiplication-jungle': <MultiplicationJungleGame />,
    'multiplication-sudoku': <MultiplicationSudokuGame />,
    'cleanup-quest': <CleanupQuestGame />,
    // SEL Games
    'sel-teddy-playground': <SELTeddyPlayground />,
    'sel-feelings-faces': <SELFeelingsFaces />,
    'sel-bunny-choices': <SELBunnyChoices />,
    'sel-calm-bubbles': <SELCalmBubbles />,
  };
  return games[gameId] || <Navigate to="/dashboard" />;
}

function App() {
  return (
    <GameProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-jungle-100 via-safari-50 to-jungle-200">
          <AppRoutes />
          <Avatar />
          <NavigationBar />
        </div>
      </Router>
    </GameProvider>
  );
}

export default App;